import { useEffect, useMemo, useState } from 'react';
import {
  Plus,
  Search,
  Eye,
  ArrowLeftRight,
  ArrowDownCircle,
  ArrowUpCircle,
  Settings2,
  Undo2,
  Truck,
  Trash2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card, CardHead } from '@/Components/ui/Card';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { IconButton } from '@/Components/ui/IconButton';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { StatusBadge, Chip } from '@/Components/ui/Badge';
import type { Tone } from '@/Components/ui/types';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Components/ui/Dialog';
import estoqueMovimentacaoService, {
  type EstoqueMovimentacao,
  type EstoqueMovimentacaoTipo,
} from '@/services/estoqueMovimentacao';

const TIPO_LABELS: Record<EstoqueMovimentacaoTipo, string> = {
  ENTRADA: 'Entrada',
  SAIDA: 'Saída',
  TRANSFERENCIA: 'Transferência',
  AJUSTE: 'Ajuste',
  PRODUCAO: 'Produção',
  DEVOLUCAO: 'Devolução',
};
const TIPO_TONE: Record<EstoqueMovimentacaoTipo, Tone> = {
  ENTRADA: 'success',
  SAIDA: 'danger',
  TRANSFERENCIA: 'info',
  AJUSTE: 'warning',
  PRODUCAO: 'purple',
  DEVOLUCAO: 'neutral',
};
const TIPO_ICONS: Record<EstoqueMovimentacaoTipo, typeof ArrowDownCircle> = {
  ENTRADA: ArrowDownCircle,
  SAIDA: ArrowUpCircle,
  TRANSFERENCIA: ArrowLeftRight,
  AJUSTE: Settings2,
  PRODUCAO: Truck,
  DEVOLUCAO: Undo2,
};

function formatNumber(v: number | string | null | undefined) {
  const n = Number(v) || 0;
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}

function formatDateTime(v?: string | null) {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleString('pt-BR');
}

function getActionErrorMessage(error: unknown, fallback: string) {
  const data = (error as { response?: { data?: { error?: string; message?: string } } }).response?.data;
  return data?.error || data?.message || fallback;
}

export default function MovimentacoesPage() {
  const navigate = useNavigate();
  const [movimentacoes, setMovimentacoes] = useState<EstoqueMovimentacao[]>([]);
  const [loading, setLoading] = useState(false);
  const [processando, setProcessando] = useState<string | null>(null);
  const [erroAcao, setErroAcao] = useState<string | null>(null);

  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  const [dialogExcluir, setDialogExcluir] = useState(false);
  const [itemExcluir, setItemExcluir] = useState<EstoqueMovimentacao | null>(null);

  const carregar = async () => {
    setLoading(true);
    try {
      setMovimentacoes(await estoqueMovimentacaoService.listar({ tipo: filtroTipo || undefined }));
    } catch (error) {
      console.error('Erro ao carregar movimentações de estoque', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroTipo]);

  const abrirExcluir = (item: EstoqueMovimentacao) => {
    setErroAcao(null);
    setItemExcluir(item);
    setDialogExcluir(true);
  };

  const confirmarExclusao = async () => {
    if (!itemExcluir) return;

    setProcessando(itemExcluir.id);
    try {
      await estoqueMovimentacaoService.excluir(itemExcluir.id);
      setDialogExcluir(false);
      setItemExcluir(null);
      await carregar();
    } catch (error) {
      console.error('Erro ao excluir movimentação de estoque', error);
      setErroAcao(getActionErrorMessage(error, 'Não foi possível excluir a movimentação.'));
    } finally {
      setProcessando(null);
    }
  };

  const itensFiltrados = useMemo(() => {
    let resultado = movimentacoes;
    if (busca) {
      const termo = busca.toLowerCase();
      resultado = resultado.filter(
        (m) => (m.produto_nome || '').toLowerCase().includes(termo) || (m.documento || '').toLowerCase().includes(termo)
      );
    }
    if (filtroDataInicio) {
      resultado = resultado.filter((m) => m.data_movimento >= filtroDataInicio);
    }
    if (filtroDataFim) {
      resultado = resultado.filter((m) => m.data_movimento <= `${filtroDataFim}T23:59:59`);
    }
    return resultado;
  }, [movimentacoes, busca, filtroDataInicio, filtroDataFim]);

  return (
    <div className="nx-page">
      <PageHeader
        icon={<ArrowLeftRight size={21} strokeWidth={2} />}
        title="Movimentações de Estoque"
        description="Lançamentos de entrada, saída, transferência e ajuste de estoque, com sincronização com o ERP legado."
        actions={
          <Button type="button" onClick={() => navigate('/producao/estoque/movimentacoes/novo')}>
            <Plus size={16} strokeWidth={2.2} />
            Nova Movimentação
          </Button>
        }
      />

      {erroAcao ? (
        <div className="nx-form-alert nx-form-alert--error" role="alert">
          {erroAcao}
        </div>
      ) : null}

      <Card className="nx-page-card nx-sngpc-listing-card">
        <CardHead
          title="Listagem"
          actions={
            <div className="nx-card-head-actions">
              <div className="nx-search-field">
                <Search size={15} strokeWidth={2} aria-hidden />
                <input
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar produto/documento..."
                />
              </div>
              <Select filter value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
                <option value="">Todos os tipos</option>
                {Object.entries(TIPO_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
              <Input
                type="date"
                value={filtroDataInicio}
                onChange={(e) => setFiltroDataInicio(e.target.value)}
                title="Data Início"
              />
              <Input
                type="date"
                value={filtroDataFim}
                onChange={(e) => setFiltroDataFim(e.target.value)}
                title="Data Fim"
              />
            </div>
          }
        />

        {!loading && itensFiltrados.length === 0 ? (
          <EmptyState message="Nenhuma movimentação encontrada." />
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Produto</th>
                  <th>Lote</th>
                  <th>Quantidade</th>
                  <th>Data</th>
                  <th>Documento</th>
                  <th className="nx-col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="nx-table-loading">Carregando…</td>
                  </tr>
                ) : (
                  itensFiltrados.map((item) => {
                    const Icon = TIPO_ICONS[item.tipo] || ArrowLeftRight;
                    const positivo = ['ENTRADA', 'AJUSTE', 'PRODUCAO', 'DEVOLUCAO'].includes(item.tipo) && Number(item.quantidade) > 0;
                    return (
                      <tr key={item.id}>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <Icon size={14} strokeWidth={2} aria-hidden />
                            <StatusBadge label={TIPO_LABELS[item.tipo]} tone={TIPO_TONE[item.tipo]} />
                          </span>
                        </td>
                        <td>
                          <div>{item.produto_nome}</div>
                          <span style={{ fontSize: '0.75rem', color: 'var(--nx-muted)' }}>{item.produto_codigo}</span>
                        </td>
                        <td>
                          {item.lote_numero ? <Chip>{item.lote_numero}</Chip> : <span style={{ color: 'var(--nx-muted)' }}>-</span>}
                        </td>
                        <td>
                          <span style={{ fontWeight: 600, color: positivo ? 'var(--nx-ok)' : 'var(--nx-danger)' }}>
                            {item.tipo === 'SAIDA' ? '-' : '+'}{formatNumber(item.quantidade)}
                          </span>
                        </td>
                        <td>{formatDateTime(item.data_movimento)}</td>
                        <td>{item.documento || '-'}</td>
                        <td className="nx-col-acoes">
                          <div className="nx-table-actions-group nx-table-actions-group--buttons">
                            <IconButton label="Detalhes" onClick={() => navigate(`/producao/estoque/movimentacoes/${item.id}/visualizar`)}>
                              <Eye size={15} strokeWidth={1.9} aria-hidden />
                            </IconButton>
                            <IconButton
                              label="Excluir"
                              variant="danger"
                              onClick={() => abrirExcluir(item)}
                              disabled={processando === item.id}
                            >
                              <Trash2 size={15} strokeWidth={1.9} aria-hidden />
                            </IconButton>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={dialogExcluir} onOpenChange={(next) => !processando && setDialogExcluir(next)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir Movimentação</DialogTitle>
            <DialogDescription>
              Ao excluir, o estoque será estornado para a situação anterior desta movimentação.
            </DialogDescription>
          </DialogHeader>
          {itemExcluir ? (
            <div className="nx-detail-grid">
              <div>
                <span className="nx-field-label">Tipo</span>
                <div>{TIPO_LABELS[itemExcluir.tipo]}</div>
              </div>
              <div>
                <span className="nx-field-label">Produto</span>
                <div>{itemExcluir.produto_nome || '-'}</div>
              </div>
              <div>
                <span className="nx-field-label">Quantidade</span>
                <div>{formatNumber(itemExcluir.quantidade)}</div>
              </div>
              <div>
                <span className="nx-field-label">Data</span>
                <div>{formatDateTime(itemExcluir.data_movimento)}</div>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={!!processando}>Cancelar</Button>
            </DialogClose>
            <Button
              type="button"
              variant="danger"
              loading={processando === itemExcluir?.id}
              loadingText="Excluindo..."
              onClick={confirmarExclusao}
            >
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
